<template>
    <div class="st-page">
        <div class="st-wrapper">

            <!-- Error state -->
            <div v-if="initError" class="st-card">
                <div class="st-error-icon">
                    <i class="bi bi-exclamation-triangle-fill"></i>
                </div>
                <h3 class="st-heading">{{ $t('integrations.support.errorGeneric') }}</h3>
                <p class="st-sub">{{ initError }}</p>
                <button class="st-btn st-btn--secondary mt-4" @click="deny">
                    {{ $t('integrations.support.cancel') }}
                </button>
            </div>

            <!-- Success state -->
            <div v-else-if="submitted" class="st-card st-card--success">
                <div class="st-success-icon">
                    <i class="bi bi-check-circle-fill"></i>
                </div>
                <h3 class="st-heading">{{ $t('integrations.support.successTitle') }}</h3>
                <p class="st-sub">{{ $t('integrations.support.successMessage', { number: ticketNumber || '' }) }}</p>
                <button class="st-btn st-btn--primary mt-4" @click="finish">
                    {{ $t('close') }}
                </button>
            </div>

            <!-- Form card -->
            <div v-else class="st-card">

                <div class="st-header">
                    <div class="st-header-icon">
                        <i class="bi bi-headset"></i>
                    </div>
                    <div>
                        <h2 class="st-title">{{ $t('integrations.support.createTicket') }}</h2>
                        <p class="st-sub mb-0">{{ $t('integrations.support.descriptionPlaceholder') }}</p>
                    </div>
                </div>

                <hr class="st-divider" />

                <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = null" class="mb-3">
                    {{ error }}
                </b-alert>

                <b-form @submit.prevent="createTicket">
                    <b-form-group
                        :label="$t('integrations.support.subject')"
                        label-for="st-subject"
                        class="mb-3"
                    >
                        <b-form-input
                            id="st-subject"
                            v-model="form.subject"
                            :placeholder="$t('integrations.support.subjectPlaceholder')"
                            required
                            maxlength="500"
                        />
                    </b-form-group>

                    <b-row>
                        <b-col cols="12" md="6">
                            <b-form-group
                                :label="$t('integrations.support.topic')"
                                label-for="st-topic"
                                class="mb-3"
                            >
                                <b-form-select
                                    id="st-topic"
                                    v-model="form.topic"
                                    :options="topicOptions"
                                    required
                                />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group
                                :label="$t('integrations.support.priority')"
                                label-for="st-priority"
                                class="mb-3"
                            >
                                <b-form-select
                                    id="st-priority"
                                    v-model="form.priority"
                                    :options="priorityOptions"
                                    required
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-form-group
                        :label="$t('integrations.support.description')"
                        label-for="st-description"
                        class="mb-3"
                    >
                        <b-form-textarea
                            id="st-description"
                            v-model="form.description"
                            :placeholder="$t('integrations.support.descriptionPlaceholder')"
                            rows="5"
                            required
                            maxlength="10000"
                        />
                    </b-form-group>

                    <hr class="st-divider" />

                    <div class="d-flex justify-content-between align-items-center">
                        <button
                            type="button"
                            class="st-btn st-btn--secondary"
                            @click="deny"
                            :disabled="saving"
                        >
                            {{ $t('integrations.support.cancel') }}
                        </button>
                        <button
                            type="submit"
                            class="st-btn st-btn--primary"
                            :disabled="saving"
                        >
                            <b-spinner v-if="saving" small class="mr-2" />
                            <i v-else class="bi bi-cursor-fill mr-2"></i>
                            {{ saving ? $t('integrations.support.submitting') : $t('integrations.support.submit') }}
                        </button>
                    </div>
                </b-form>
            </div>

        </div>
    </div>
</template>

<script>
var TOPICS_BY_TIER = {
    free: ['general'],
    standard: ['general', 'technical'],
    pro: ['general', 'technical', 'billing', 'account', 'priority_support']
}

var PRIORITIES_BY_TIER = {
    free: ['normal'],
    standard: ['low', 'normal', 'high'],
    pro: ['low', 'normal', 'high', 'urgent']
}

export default {
    name: 'SupportCreateTicket',

    data: function() {
        return {
            tier: 'free',
            form: {
                subject: this.$route.query.subject || '',
                description: this.$route.query.description || '',
                topic: this.$route.query.topic || 'general',
                priority: this.$route.query.priority || 'normal'
            },
            saving: false,
            submitted: false,
            ticketNumber: null,
            error: null,
            initError: null
        }
    },

    computed: {
        topicOptions: function() {
            var allowed = TOPICS_BY_TIER[this.tier] || ['general']
            var self = this
            return allowed.map(function(t) {
                return { value: t, text: self.$t('integrations.support.topics.' + t) }
            })
        },

        priorityOptions: function() {
            var allowed = PRIORITIES_BY_TIER[this.tier] || ['normal']
            var self = this
            return allowed.map(function(p) {
                return { value: p, text: self.$t('integrations.support.priorities.' + p) }
            })
        }
    },

    mounted: function() {
        this.loadTierInfo()
    },

    methods: {
        loadTierInfo: function() {
            var self = this
            self.$integrationApi.get('/protokol-support/support/info').then(function(resp) {
                if (resp.data && resp.data.tier) {
                    self.tier = resp.data.tier
                }
                var allowedTopics = TOPICS_BY_TIER[self.tier] || ['general']
                if (allowedTopics.indexOf(self.form.topic) === -1) {
                    self.form.topic = allowedTopics[0]
                }
                var allowedPriorities = PRIORITIES_BY_TIER[self.tier] || ['normal']
                if (allowedPriorities.indexOf(self.form.priority) === -1) {
                    self.form.priority = allowedPriorities[0]
                }
            }).catch(function() {
                // defaults to free tier
            })
        },

        createTicket: function() {
            var self = this
            self.saving = true
            self.error = null

            self.$integrationApi.post('/protokol-support/support/tickets', {
                subject: self.form.subject,
                description: self.form.description,
                topic: self.form.topic,
                priority: self.form.priority
            }).then(function(resp) {
                self.saving = false
                self.submitted = true
                self.ticketNumber = (resp.data && resp.data.ticket_number) ? resp.data.ticket_number : null
                var msg = {
                    type: 'support_ticket_created',
                    ticket: resp.data || null
                }
                if (window.parent !== window) {
                    window.parent.postMessage(msg, '*')
                }
            }).catch(function(err) {
                self.saving = false
                var code = err.response && err.response.data && err.response.data.code
                if (code === 4029) {
                    self.error = self.$t('integrations.support.limitExceeded')
                } else if (err.response && err.response.data && err.response.data.message) {
                    self.error = err.response.data.message
                } else {
                    self.error = self.$t('integrations.support.errorGeneric')
                }
            })
        },

        deny: function() {
            if (window.parent !== window) {
                window.parent.postMessage({ type: 'support_ticket_denied' }, '*')
                return
            }
            window.close()
        },

        finish: function() {
            if (window.parent !== window) {
                window.parent.postMessage({ type: 'support_ticket_close' }, '*')
                return
            }
            window.close()
        }
    }
}
</script>

<style scoped>
.st-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-page, #0f1117);
    padding: 2rem 1rem;
}

.st-wrapper {
    width: 100%;
    max-width: 560px;
}

.st-card {
    background: var(--bg-card, #1a1d27);
    border: 1px solid var(--border-default, rgba(255,255,255,0.08));
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.6);
    color: var(--text-primary, #e8eaf0);
}

.st-card--success {
    text-align: center;
}

.st-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0;
}

.st-header-icon {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: var(--bg-card-secondary, rgba(255,255,255,0.06));
    border: 1px solid var(--border-default, rgba(255,255,255,0.08));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: var(--primary, #5b73e8);
    flex-shrink: 0;
}

.st-title {
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0 0 0.25rem 0;
    color: var(--text-primary, #e8eaf0);
    line-height: 1.2;
}

.st-heading {
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0.75rem 0 0.5rem 0;
    color: var(--text-primary, #e8eaf0);
}

.st-sub {
    font-size: 0.875rem;
    color: var(--text-muted, rgba(255,255,255,0.6));
    margin: 0;
    line-height: 1.5;
}

.st-divider {
    border-color: var(--border-default, rgba(255,255,255,0.08));
    margin: 1.25rem 0;
}

.st-error-icon,
.st-success-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}

.st-error-icon { color: #f87171; }
.st-success-icon { color: #4ade80; }

.st-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: opacity 0.15s;
}

.st-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.st-btn--primary {
    background: var(--primary, #5b73e8);
    color: #fff;
}

.st-btn--primary:hover:not(:disabled) {
    opacity: 0.88;
}

.st-btn--secondary {
    background: transparent;
    border: 1px solid var(--border-default, rgba(255,255,255,0.2));
    color: var(--text-primary, #e8eaf0);
}

.st-btn--secondary:hover:not(:disabled) {
    background: rgba(255,255,255,0.06);
}
</style>
